<template>
  <!-- HOMEPAGE -->
  <PagePortal
    id="userHomepage"
    :title="seoData?.title || $t('general.cezma')"
    :description="seoData?.description"
    :keywords="seoData?.keywords"
    :imageAlt="seoData?.imageAltText"
  >
    <!-- View Content Modal -->
    <Modal
      rounded="42px"
      :isOpen="isOpencontentModal"
      :modalTitle="$t('general.notification_content')"
      :changeModalState="toggleContentModal"
      :hideHeadersBorder="true"
    >
      <div class="p-6 lg:p-8">
        <h5 class="font-bold text-xl md:text-2xl mb-6 text-gray-900">
          {{ originalData?.notification?.title }}
        </h5>

        <div
          class="text-gray-600 text-base leading-relaxed mb-8 whitespace-pre-wrap"
        >
          {{ originalData?.notification?.body }}
        </div>

        <div class="flex justify-end">
          <button
            @click="toggleContentModal(false, 'close')"
            class="text-primary border-2 border-primary hover:bg-primary hover:text-white transition-colors duration-200 rounded-lg text-center font-medium min-w-fit px-8 py-3"
          >
            {{ $t("general.close") }}
          </button>
        </div>
      </div>
    </Modal>
    <!-- Reminder renew modal -->
    <ChangePackageModal
      v-if="reminderQuotaModal.isOpen"
      ref="reminderChangePackage"
      :isQuotaModalOpen="reminderQuotaModal.isOpen"
      :toggleChangingQuotaModal="toggleReminderQuotaModal"
      :package_price="reminderQuotaModal.package_price"
      :is_downgraded="reminderQuotaModal.is_downgraded"
      :packageProductsCount="reminderQuotaModal.products_count"
      :receivedProductsCount="reminderReceivedProductsCount"
      :productsList="reminderProductsList"
      :currentStoreId="reminderQuotaModal.store_id"
      :updateData="refreshHomepageAfterRenew"
      :isLoadingProducts="reminderProductsLoading"
      :whatsappLink="reminderWhatsappLink"
      :hideMainModal="true"
    />
    <!-- top categories - shown only on the desktop   -->
    <div>
      <div
        class="sm:app-container sm:sticky sm:z-10 lg:top-[70px] md:top-[55px]"
      >
        <!-- <MobileInstallationDialog/> -->
        <ReminderNotification
          v-if="showReminderNotifications"
          :notificationData="originalData.notification"
          @openRenewModal="handleReminderRenew"
        />
        <HomeCategories
          :categoriesList="categoriesList"
          :isLoading="isLoading || isLoadingCategories"
          :listLength="categoriesList ? categoriesList.length : 0"
          :savedCategoriesLength="savedCategoriesLength"
          class="sm:shadow-[0_8px_20px_-15px_rgba(0,0,0,0.3)] hidden lg:block bg-[#D2D2D21A] z-1 before:absolute before:content-[''] before:bg-white before:w-full before:h-full before:z-[-1] before:top-0 before:bottom-0 before:left-0 before:right-0"
        />
      </div>
      <div class="space-y-7 md:space-y-10 lg:space-y-14 mb-9 md:mb-20">
        <!-- Images slider -->
        <section>
          <div class="slider-container-full-width-mobile">
            <CarouselSlider
              :isLoading="isLoading"
              :slidersList="
                originalData && originalData.sliders ? originalData.sliders : []
              "
            />
          </div>
        </section>
        <!-- Categories slider -->
        <section class="app-container-full-width-mobile">
          <CategoriesSlider
            :categoriesList="categoriesList"
            :isLoading="isLoading || isLoadingCategories"
            :nameKey="
              currentlySelectedLanguage === 'en' ? 'name_en' : 'name_ar'
            "
          />
        </section>
          <GoogleAdsBanner />
        <!-- Exclusive Offers -->
        <section
          v-if="exclusiveOffersList?.length > 0"
          class="app-container-full-width-mobile"
        >
          <ExclusiveOffersSlider
            :exclusiveOffersList="exclusiveOffersList"
            :isLoading="isLoading || isLoadingExclusiveOffers"
          />
        </section>
        <section class="app-container-full-width-mobile">
          <!-- Popular stores -->
          <HomePopularStores
            :changeFilterByVerifiedStoresOnlyState="
              changeFilterByVerifiedStoresOnlyState
            "
            :filterByVerifiedStoresOnly="filterByVerifiedStoresOnly"
            :isInitialLoading="isLoading"
            :isLoading="isLoading || isLoadingStores"
            :storesList="storesList"
            :title="
              filterByVerifiedStoresOnly
                ? $t('user.popular_verified_stores')
                : $t('user.popular_stores')
            "
          />
        </section>
        <!-- Trendy products -->
        <section>
          <NewHomeSections
            :changeProductByTypeValue="changeProductByTypeValue"
            :filterProductsByType="filterProductsByType"
            :hasReachedFullPages="currentPage >= maxPages"
            :hasReachedFullProductPages="hasReachedFullProductPages"
            :isInitiallyLoadingProducts="isInitiallyLoadingProducts"
            :isLoading="isLoading || isLoadingProducts"
            :isLoadingMore="isLoadingMore"
            :isUpdatingProducts="isUpdatingProducts"
            :itemsList="productsList"
            :loadMoreData="handleInfiniteScrollLoadMore"
            :loadProductsSortedBy="
              ({ sortBy }) =>
                getProducts({
                  loadMore: false,
                  sortProductsBy: sortBy,
                  updateMode: true,
                })
            "
            :enableAutoScroll="autoScrollEnabled"
            :manualLoadMore="handleManualLoadMore"
            :sortProductsBy="sortProductsBy"
            :portalPage="'homepage'"
            @saveProductCardPosition="saveProductCardPosition"
          />
        </section>
      </div>
    </div>
  </PagePortal>
</template>

<script>
import {
  CarouselSlider,
  CategoriesSlider,
  ChangePackageModal,
  ExclusiveOffersSlider,
  HomeCategories,
  HomePopularStores,
  MobileInstallationDialog,
  NewHomeSections,
  PagePortal,
  ReminderNotification,
  SectionTitle,
  Modal,
  GoogleAdsBanner,
} from "@/components";
import { useGlobalStore } from "@/store/Modules/global.js";
import { mapActions, mapState } from "pinia";
import {
  APIREQUESTS,
  checkPaginationEnd,
  MAINROLES,
  useLocalStorage,
  getFirebaseToken,
  generateWhatsappLink,
} from "@/helpers";
import { safeProductListCache } from "@/utils/productListCache";
import HomeCategoryItem from "~/components/user/homepage/homeCategories/MainCategoryItem.vue";
import HomeSubCategoryItem from "~/components/user/homepage/homeCategories/SubCategoryItem.vue";
import { usePageSeo } from "@/composables/usePageSeo";

export default {
  name: "homepage",
  async setup() {
    definePageMeta({
      layout: "user",
    });

    const seoData = await usePageSeo("home");

    return {
      seoData,
    };
  },
  data() {
    return {
      enableAds: false,
      isOpencontentModal: false,
      abortController: new AbortController(),
      filterByVerifiedStoresOnly: 0,
      storesList: [],
      originalData: {
        categories: [],
        products: [],
        exclusiveOffers: [],
        hasReachedFullProductPages: false,
        sortProductsBy: "",
        activeProductType: "",
        filterProductsByType: null,
      },
      currentPage: 1,
      maxPages: 10,
      currentProductsPage: 1,
      isLoading: true,
      isLoadingMore: false,
      isLoadingCategories: false,
      isLoadingStores: false,
      isLoadingProducts: false,
      isUpdatingProducts: false,
      isLoadingExclusiveOffers: false,
      isInitiallyLoadingProducts: false,
      currentLocation: "",
      isHomePageMounted: false,
      cachedDataDisplayed: false,
      autoLoadPagesCount: 0,
      maxAutoScrollPages: 5,
      autoScrollDisabled: false,
      reminderQuotaModal: {
        isOpen: false,
        package_price: 0,
        is_downgraded: false,
        products_count: 0,
        current_products_count: 0,
        store_id: null,
      },
      reminderProductsList: [],
      reminderProductsLoading: false,
      reminderWhatsappLink: null,
      _isRestoringPosition: false,
      _pendingProductRestore: null,
      _restoreRetryCount: 0,
    };
  },
  components: {
    GoogleAdsBanner,
    Modal,
    HomeSubCategoryItem,
    HomeCategoryItem,
    ChangePackageModal,
    PagePortal,
    HomeCategories,
    NewHomeSections,
    ReminderNotification,
    MobileInstallationDialog,
    CarouselSlider,
    SectionTitle,
    HomePopularStores,
    CategoriesSlider,
    ExclusiveOffersSlider,
  },
  computed: {
    ...mapState(useGlobalStore, {
      savedHomepageData: (store) => store?.getSavedHomepageData || [],
      localStorageCopy: (store) => store?.localStorageCopy || {},
      layout: (store) => store?.layout || {},
    }),
    showReminderNotifications() {
      return !this.isLoading && this.originalData?.notification?.id;
    },
    categoriesList() {
      return this.originalData?.categories || [];
    },
    savedCategoriesLength() {
      return this.savedHomepageData?.receivedData?.data?.categories?.length;
    },
    productsList() {
      return this.originalData?.products;
    },
    exclusiveOffersList() {
      return this.originalData?.exclusiveOffers || [];
    },
    sortProductsBy() {
      return this.originalData?.sortProductsBy || "";
    },
    filterProductsByType() {
      return this.originalData?.filterProductsByType;
    },
    hasReachedFullProductPages() {
      return !!this.originalData?.hasReachedFullProductPages;
    },
    autoScrollEnabled() {
      return (
        !this.autoScrollDisabled &&
        this.autoLoadPagesCount < this.maxAutoScrollPages &&
        !this.hasReachedFullProductPages
      );
    },
    warningNotification() {
      if (this.originalData.notification?.type === "warning") {
        if (
          this.useLocalStorage({
            type: "get",
            key: "warning_notification_closed",
          }) === true
        ) {
          return;
        } else {
          this.toggleContentModal(true);
        }
      }
    },
    reminderReceivedProductsCount() {
      return (
        this.reminderQuotaModal.current_products_count ||
        this.reminderProductsList.length ||
        0
      );
    },
  },
  methods: {
    ...mapActions(useGlobalStore, ["updateHomepageData"]),
    resetAutoScrollState() {
      this.autoLoadPagesCount = 0;
      this.autoScrollDisabled = false;
    },
    disableAutoScroll() {
      this.autoScrollDisabled = true;
    },
    incrementAutoLoadCount() {
      this.autoLoadPagesCount = Math.min(
        this.autoLoadPagesCount + 1,
        this.maxAutoScrollPages
      );
      if (this.autoLoadPagesCount >= this.maxAutoScrollPages) {
        this.disableAutoScroll();
      }
    },
    handleInfiniteScrollLoadMore() {
      if (!this.autoScrollEnabled || this.isLoadingMore) {
        this.disableAutoScroll();
        return;
      }
      this.getProducts({
        loadMore: true,
        updateMode: true,
        loadTrigger: "auto",
      });
    },
    handleManualLoadMore() {
      if (this.isLoadingMore || this.hasReachedFullProductPages) {
        return;
      }
      // Reset auto-scroll state to enable infinity scrolling for another 5 pages
      this.resetAutoScrollState();
      // Trigger first page load immediately for better UX
      this.getProducts({
        loadMore: true,
        updateMode: true,
        loadTrigger: "auto", // Use "auto" so incrementAutoLoadCount() is called
      });
    },
    toggleContentModal(state, type) {
      this.isOpencontentModal = state;
      if (type === "close") {
        this.useLocalStorage({
          type: "set",
          key: "warning_notification_closed",
          value: true,
        });
        this.isOpencontentModal = false;
      }
    },
    refreshHomepageAfterRenew() {
      this.fetchNewHome({ loadMore: false });
    },
    toggleReminderQuotaModal(newState) {
      const nextState =
        typeof newState === "boolean"
          ? newState
          : !this.reminderQuotaModal.isOpen;
      if (!nextState) {
        this.reminderQuotaModal = {
          isOpen: false,
          package_price: 0,
          is_downgraded: false,
          products_count: 0,
          current_products_count: 0,
          store_id: null,
        };
        this.reminderProductsList = [];
        this.reminderProductsLoading = false;
        return;
      }
      this.reminderQuotaModal = {
        ...this.reminderQuotaModal,
        isOpen: nextState,
      };
    },
    async handleReminderRenew(payload) {
      const storeId = payload?.storeId || payload?.notification?.type_id;
      if (!storeId) {
        this.notify({
          type: "error",
          msg: this.$t("general.failed"),
        });
        return;
      }
      const userRole =
        this.useLocalStorage({ type: "get", key: "user" })?.role || "";
      const allowedRoles = ["reseller", "moderator_reseller"];
      if (!allowedRoles.includes(userRole)) {
        this.notify({
          type: "error",
          msg: this.$t("general.not_allowed"),
        });
        return;
      }
      const whatsapp = this.layout?.social_media?.whatsapp;
      this.reminderWhatsappLink = whatsapp
        ? generateWhatsappLink({
            phone_number: whatsapp,
            message: this.$t("general.whatsapp_message"),
            link: "",
          })
        : null;
      this.reminderQuotaModal = {
        ...this.reminderQuotaModal,
        isOpen: true,
        store_id: storeId,
      };
      this.reminderProductsLoading = true;
      try {
        const [storeDetails, productsResponse] = await Promise.allSettled([
          this.makeRequest({
            specificRole: userRole,
            owner: MAINROLES.admin,
            url: `${APIREQUESTS.panel.getStores.url}/${storeId}`,
            method: APIREQUESTS.panel.getStores.method,
          }),
          this.makeRequest({
            specificRole: userRole,
            owner: MAINROLES.admin,
            url: `${APIREQUESTS.panel.getProducts.url}?store_id=${storeId}&paginate=0`,
            method: APIREQUESTS.panel.getProducts.method,
          }),
        ]);
        if (
          storeDetails.status !== "fulfilled" ||
          !storeDetails.value?.status
        ) {
          throw new Error(
            storeDetails.value?.message || this.$t("general.failed")
          );
        }
        const storeData = storeDetails.value?.data || {};
        this.reminderQuotaModal = {
          ...this.reminderQuotaModal,
          isOpen: true,
          package_price:
            storeData.package_price || storeData.package?.price || 0,
          is_downgraded: !!storeData.is_downgraded,
          products_count: Number(storeData.products_count) || 0,
          current_products_count:
            Number(storeData.current_products_count) || 0,
          store_id: storeData.id || storeId,
        };
        if (
          productsResponse.status === "fulfilled" &&
          productsResponse.value?.status
        ) {
          this.reminderProductsList = productsResponse.value.data || [];
        } else {
          this.reminderProductsList = [];
        }
      } catch (error) {
        this.toggleReminderQuotaModal(false);
        this.notify({
          type: "error",
          msg: error?.message || this.$t("general.failed"),
        });
        return;
      } finally {
        this.reminderProductsLoading = false;
      }
      this.$nextTick(() => {
        this.$refs.reminderChangePackage?.startRenewPaymentFlow?.();
      });
    },
    async refreshFirebaseToken() {
      console.log("refreshFirebaseToken");
      const isUserLogged =
        this.useLocalStorage({ type: "get", key: "user" })?.token || false;
      if (!isUserLogged) {
        console.log("user not logged");
        return;
      }

      // first get current firebase token
      let firebaseToken = "";
      try {
        firebaseToken = await getFirebaseToken();
      } catch (err) {
        console.log("error getting firebase token", err);
      }
      console.log("firebaseToken", firebaseToken);
      if (!firebaseToken) return;
      // then send firebase token
      this.makeRequest({
        owner: MAINROLES.user,
        url: APIREQUESTS.general.refreshFirebase.url,
        method: APIREQUESTS.general.refreshFirebase.method,
        formData: JSON.stringify({ firebase_token: firebaseToken }),
        includeUID: true,
      })
        .then((res) => {
          if (!res?.status) {
            //not ok
          } else {
            //ok
          }
        })
        .catch((err) => {});
    },
    async getProfile() {
      const isUserLogged =
        this.useLocalStorage({ type: "get", key: "user" })?.token || false;
      let role = this.useLocalStorage({ type: "get", key: "user" })?.role;
      // ...(isUserLogged && {owner: MAINROLES.user})
      this.makeRequest({
        specificRole: role,
        owner: MAINROLES.user,
        method: APIREQUESTS.user.getMyProfileInfo.method,
        url: APIREQUESTS.user.getMyProfileInfo.url,
      })
        .then((res) => {
          this.isLoading = false;
          console.log("role:", role);
          // failed
          if (!res?.status) {
            this.notify({
              type: "error",
              msg: res.message,
            });
          } else {
            // succeeded
            let user = this.useLocalStorage({ type: "get", key: "user" }) || {};
            let admin =
              this.useLocalStorage({ type: "get", key: "admin" }) || {};

            if (role === "moderator_reseller") {
              const user_after_update = {
                ...user,
                permissions: res.data.permissions,
              };
              const admin_after_update = {
                ...admin,
                permissions: res.data.permissions,
              };
              this.useLocalStorage({
                type: "set",
                key: "user",
                value: user_after_update,
              });
              this.useLocalStorage({
                type: "set",
                key: "admin",
                value: admin_after_update,
              });
            } else {
              const user_storage_token =
                this.useLocalStorage({ type: "get", key: "user" }).token || "";
              const user_after_update = {
                ...res.data,
                token: user_storage_token,
              };
              this.useLocalStorage({
                type: "set",
                key: "user",
                value: user_after_update,
              });
            }
            // Set user language
            if (res.data?.language) {
              this.useLocalStorage({
                type: "set",
                key: "language",
                value: res.data.language,
              });
            }
          }
        })
        .catch((err) => {
          this.isLoading = false;
          this.notify({
            type: "error",
            msg: err?.data?.message || err,
          });
        });
    },
    fetchNewHome({ loadMore = false }) {
      return new Promise((resolve, reject) => {
        // the load more feature is not being used here
        if (!process.client) {
          reject();
          return;
        }

        loadMore ? (this.isLoadingMore = true) : (this.isLoading = true);

        let queryObj = {
          ...(loadMore && {
            page: this.currentPage + 1,
          }),
          ...(this.currentLocation && {
            location: this.currentLocation,
          }),
        };
        let queryStr = Object.entries(queryObj)
          .map((item, i) => (i == 0 ? item.join("=") : `&${item.join("=")}`))
          .join("");

        //check if user logged or not to get products depend on user
        const isUserLogged =
          this.useLocalStorage({ type: "get", key: "user" })?.token || false;
        this.makeRequest({
          ...(isUserLogged && { owner: MAINROLES.user }),
          url: `${APIREQUESTS.user.getNewHomepageData.url}?${queryStr}`,
          method: APIREQUESTS.user.getNewHomepageData.method,
          abortControllerSignal: this.abortController.signal,
          includeUID: true,
        })
          .then((res) => {
            if (res?.status) {
              resolve();
              isUserLogged &&
                this.updateUnreadMessages(res?.data?.unread_messages || 0);
              let receivedData = {};
              if (loadMore && this.originalData?.products?.length > 0) {
                const sectionNodes = document.querySelectorAll(
                  "#sectionItemsList > section"
                );
                const lastSectionNode = sectionNodes?.[sectionNodes.length - 1];
                lastSectionNode
                  ?.querySelector(".more__products--button")
                  ?.focus();

                receivedData = {
                  ...this.originalData,
                  ...(res.data || []),
                };
              } else {
                receivedData =
                  {
                    ...res.data,
                  } || {};
              }
              this.originalData = receivedData;
              // saves the results in the global storage
              // this.updateHomepageData({ receivedData: { ...res, ...{ data: receivedData } } });
              this.currentPage = parseInt(res.current_page);
              this.maxPages = parseInt(res.pages_total);
              this.warningNotification;
            } else {
              reject();
              this.notify({
                type: "error",
                msg: err?.data?.message || this.$t("general.failed"),
              });
              this.originalData = {};
            }
          })
          .catch((err) => {
            reject();
            console.error("err", err);
            if (!err || err.code !== "axios_cancelled") {
              this.notify({
                type: "error",
                msg: err?.data?.message || this.$t("general.failed"),
              });
            }
          })
          .finally(() => {
            this.isLoadingMore = false;
            this.isLoading = false;
            this.isHomePageMounted = true;
          });
      });
    },
    async sendLocationWithGetHomeApi() {
      let { lat, lng } = await this.getCurrentLocation();
      this.currentLocation = `${lat},${lng}`;
      // saves the results in the global storage
      this.updateHomepageData({
        location: {
          lat,
          lng,
        },
      });
    },
    updateUnreadMessages(unread_messages = 0) {
      let userLocal = this.useLocalStorage({ type: "get", key: "user" }) || {};
      this.useLocalStorage({
        type: "set",
        key: "user",
        value: {
          ...userLocal,
          unread_messages: unread_messages,
        },
      });
    },
    updateSortProductsBy(newSortingValue) {
      this.originalData = {
        ...this.originalData,
        sortProductsBy: newSortingValue,
      };
    },
    centerActiveGroupButton(groupButtonElement) {
      if (groupButtonElement) {
        // keep the active element in the center if it has enough peripheral space around it
        const container = groupButtonElement.closest(".slider");
        const itemLeft = groupButtonElement.offsetLeft;
        const itemWidth = groupButtonElement.offsetWidth;
        const targetScrollLeft =
          itemLeft - (container.offsetWidth / 2 - itemWidth / 2);
        container.scrollTo({
          left: +targetScrollLeft,
          behavior: "smooth",
        });
      }
    },
    getProducts({
      loadMore = false,
      sortProductsBy = this.sortProductsBy || "all",
      isInitiallyLoading = false,
      productType = this.filterProductsByType,
      updateMode = false,
      forceInitialLoad = false,
      loadTrigger = "auto",
    }) {
      // Skip cache/saved data if this is a forced initial load
      if (forceInitialLoad) {
        console.log("🔄 Force initial load: Skipping cache and saved data");
        this.cachedDataDisplayed = false;
        this.originalData.products = [];
        this.currentProductsPage = 1;
      }

      const { value: productTypeId } = productType || {};

      const isSameSortingOption = sortProductsBy == this.sortProductsBy;
      const isSameProductType = productType == this.filterProductsByType;
      
      // Store sorting change flag before updating sort (needed for scroll restoration)
      const isSortingChanged = !isSameSortingOption;

      // Enhanced early return to prevent duplicate calls
      if (
        this.isUpdatingProducts ||
        this.isLoadingMore ||
        (loadMore && this.hasReachedFullProductPages) ||
        (!updateMode ? isSameSortingOption && isSameProductType : false)
      ) {
        console.log("🚫 Blocked duplicate call:", {
          isUpdatingProducts: this.isUpdatingProducts,
          isLoadingMore: this.isLoadingMore,
          loadMore,
          hasReachedFullProductPages: this.hasReachedFullProductPages,
        });
        return;
      }

      this.updateSortProductsBy(sortProductsBy);
      if (!loadMore) {
        this.resetAutoScrollState();
      }
      
      // Clear product positions when sorting changes
      if (isSortingChanged && !loadMore) {
        // Clear product navigation context when sorting changes
        this.clearStoredProductNavigationContext();
        
        const oldCacheKey = safeProductListCache.generateCacheKey(
          this.getCurrentFilters(),
          this.sortProductsBy || "all",
          productType
        );
        // Clear product positions for the old cache key
        safeProductListCache.clearProductPositions(oldCacheKey);
        console.log("🗑️ Cleared product positions due to sorting change");
      }
      
      const isUserLogged =
        this.useLocalStorage({ type: "get", key: "user" })?.token || false;

      // Generate cache key based on current filters and sorting
      const currentFilters = this.getCurrentFilters();
      const cacheKey = safeProductListCache.generateCacheKey(
        currentFilters,
        sortProductsBy,
        productType
      );

      // Determine target page for caching
      const targetPage = loadMore ? this.currentProductsPage + 1 : 1;

      // DEBUG: Log pagination values to track cache page calculation
      console.log("🔍 Cache page calculation:", {
        loadMore,
        currentProductsPage: this.currentProductsPage,
        targetPage,
        updatingProducts: this.isUpdatingProducts,
      });

      // Check if we're coming from navigation (back/forward) vs. filter update
      const isNavigationFromBrowser = this.isNavigationFromBrowser();

      // Check if we should use cache for current request
      const shouldUseCache =
        this.shouldUseCacheForRequest(updateMode, loadMore) &&
        !forceInitialLoad;

      console.log("🔍 Cache decision:", {
        shouldUseCache,
        updateMode,
        loadMore,
        forceInitialLoad,
        cachedDataDisplayed: this.cachedDataDisplayed,
      });

      if (shouldUseCache) {
        console.log("🔍 Homepage cache check started...");
        console.log("Cache key:", cacheKey);
        console.log("Target page:", targetPage);
        console.log("Current filters:", currentFilters);
        console.log("Update mode:", updateMode);
        console.log("Is navigation from browser:", isNavigationFromBrowser);

        // Check if we have valid cache first
        const hasCache = safeProductListCache.hasValidCache(
          cacheKey,
          targetPage,
          currentFilters
        );
        console.log("Has valid cache?", hasCache);

        if (hasCache) {
          console.log("✅ Cache found, attempting smart retrieval...");

          // Check if we have a saved product position to restore
          // If yes, disable general scroll restoration and restore to product instead
          const savedProductContext = this.getStoredProductNavigationContext();
          const shouldRestoreToProduct = savedProductContext && 
                                         savedProductContext.portalPage === 'homepage' &&
                                         savedProductContext.cacheKey === cacheKey;
          
          const result = safeProductListCache.smartCacheRetrieval(
            cacheKey,
            targetPage,
            currentFilters,
            {
              // Disable general scroll restoration if we're restoring to a product, or if sorting changed
              autoRestoreScroll: !isSortingChanged && !shouldRestoreToProduct,
              autoDisplayProducts: true,
              onDataRetrieved: (cachedData) => {
                console.log(
                  "🎯 CALLBACK EXECUTED: Automatically displaying cached products:",
                  cachedData.products.length
                );

                // Use cached data
                if (loadMore && this.productsList?.length > 0) {
                  // Append to existing products for load more
                  this.originalData = {
                    ...this.originalData,
                    sortProductsBy,
                    hasReachedFullProductPages:
                      cachedData.hasReachedFullProductPages || false,
                    products: [
                      ...this.originalData?.products,
                      ...cachedData.products,
                    ],
                  };
                  // FIXED: Only update currentProductsPage for actual loadMore operations
                  this.currentProductsPage = targetPage;
                  console.log(
                    "📄 Cache loadMore: currentProductsPage set to",
                    this.currentProductsPage
                  );
                } else {
                  // Replace products for new search/filter
                  this.originalData = {
                    ...this.originalData,
                    sortProductsBy,
                    hasReachedFullProductPages:
                      cachedData.hasReachedFullProductPages || false,
                    products: cachedData.products || [],
                  };
                  // FIXED: Always reset to 1 for non-loadMore cache retrievals
                  this.currentProductsPage = 1;
                  console.log(
                    "📄 Cache replacement: currentProductsPage reset to 1"
                  );
                }
              },
              onScrollRestored: (scrollPosition) => {
                console.log(
                  "📜 Scroll position restored automatically:",
                  scrollPosition
                );
              },
              onComplete: (cachedData) => {
                console.log("🎉 Cache retrieval completed successfully");
                // Update loading states
                this.isUpdatingProducts = false;
                this.isInitiallyLoadingProducts = false;
                
                // Try to restore product position after cache restoration
                // Only if we have product context and products are loaded
                if (this.productsList && this.productsList.length > 0) {
                  this.$nextTick(() => {
                    setTimeout(() => {
                      this.tryRestoreProductPosition();
                    }, 500);
                  });
                }
              },
            }
          );

          console.log("Smart cache retrieval result:", result);

          if (result.success) {
            console.log("🎉 Smart cache retrieval successful:", result.message);
            return; // Exit early, no API call needed
          } else {
            console.log("❌ Smart cache retrieval failed:", result.reason);
          }
        } else {
          console.log("❌ No valid cache found");

          // Let's see what cache info we have
          const cacheInfo = safeProductListCache.getCacheInfo(
            cacheKey,
            targetPage,
            currentFilters
          );
          console.log("Cache info:", cacheInfo);

          // Check what's in the cache
          const allCache = safeProductListCache.getCache();
          console.log("All cache keys:", Object.keys(allCache));
          if (allCache[cacheKey]) {
            console.log(
              "Cache key exists, pages:",
              Object.keys(allCache[cacheKey])
            );
          }
        }
      } else {
        // Check if we should still try cache even in update mode (for navigation)
        const shouldTryCache =
          updateMode &&
          safeProductListCache.isCacheAvailable() &&
          !this.isInitiallyLoadingProducts && // Don't try cache on initial load
          this.productsList?.length === 0 && // Only try cache if no products are currently displayed
          this.isNavigationFromBrowser(); // Only try cache if coming from browser navigation

        if (shouldTryCache) {
          console.log(
            "🔍 Homepage cache check for navigation (update mode)..."
          );
          console.log("Cache key:", cacheKey);
          console.log("Target page:", targetPage);
          console.log("Current filters:", currentFilters);
          console.log("Update mode:", updateMode);

          // Check if we have valid cache
          const hasCache = safeProductListCache.hasValidCache(
            cacheKey,
            targetPage,
            currentFilters
          );
          console.log("Has cache for navigation?", hasCache);

          if (hasCache) {
            console.log(
              "✅ Navigation cache found, attempting smart retrieval..."
            );

            // Check if we have a saved product position to restore
            const savedProductContext = this.getStoredProductNavigationContext();
            const shouldRestoreToProduct = savedProductContext && 
                                           savedProductContext.portalPage === 'homepage' &&
                                           savedProductContext.cacheKey === cacheKey;

            const result = safeProductListCache.smartCacheRetrieval(
              cacheKey,
              targetPage,
              currentFilters,
              {
                // Disable general scroll restoration if we're restoring to a product, or if sorting changed
                autoRestoreScroll: !isSortingChanged && !shouldRestoreToProduct,
                autoDisplayProducts: true,
                onDataRetrieved: (cachedData) => {
                  console.log(
                    "🎯 CALLBACK EXECUTED: Automatically displaying cached products for navigation:",
                    cachedData.products.length
                  );

                  // Use cached data for navigation
                  this.originalData = {
                    ...this.originalData,
                    sortProductsBy,
                    hasReachedFullProductPages:
                      cachedData.hasReachedFullProductPages || false,
                    products: cachedData.products || [],
                  };
                  // FIXED: Always reset to 1 for navigation cache retrievals (not loadMore)
                  this.currentProductsPage = 1;
                  console.log(
                    "📄 Cache navigation: currentProductsPage reset to 1"
                  );
                },
                onScrollRestored: (scrollPosition) => {
                  console.log(
                    "📜 Scroll position restored automatically for navigation:",
                    scrollPosition
                  );
                },
                onComplete: (cachedData) => {
                  console.log(
                    "🎉 Navigation cache retrieval completed successfully"
                  );
                  // Update loading states
                  this.isUpdatingProducts = false;
                  this.isInitiallyLoadingProducts = false;
                  
                  // Try to restore product position after cache restoration
                  // Only if we have product context and products are loaded
                  if (this.productsList && this.productsList.length > 0) {
                    this.$nextTick(() => {
                      setTimeout(() => {
                        this.tryRestoreProductPosition();
                      }, 500);
                    });
                  }
                },
              }
            );

            console.log("Navigation cache retrieval result:", result);

            if (result.success) {
              console.log(
                "🎉 Navigation cache retrieval successful:",
                result.message
              );
              return; // Exit early, no API call needed
            } else {
              console.log(
                "❌ Navigation cache retrieval failed:",
                result.reason
              );
            }
          }
        }

        console.log("🔍 Homepage cache check skipped:", {
          updateMode,
          isCacheAvailable: safeProductListCache.isCacheAvailable(),
          shouldTryCache,
          isInitiallyLoadingProducts: this.isInitiallyLoadingProducts,
          productsListLength: this.productsList?.length,
        });
      }

      // Set loading states BEFORE making request to prevent race conditions
      if (loadMore) {
        this.isLoadingMore = true;
      } else {
        isInitiallyLoading
          ? (this.isInitiallyLoadingProducts = true)
          : (this.isUpdatingProducts = true);
      }

      // Calculate the page to request
      const requestPage = loadMore ? this.currentProductsPage + 1 : 1;

      console.log("🔍 Pagination debug:", {
        loadMore,
        currentProductsPage: this.currentProductsPage,
        requestPage,
        isLoadingMore: this.isLoadingMore,
        isUpdatingProducts: this.isUpdatingProducts,
      });

      const canSortProducts = sortProductsBy && sortProductsBy !== "all";
      let queryObj = {
        page: requestPage, // ALWAYS include page parameter
        ...(canSortProducts && {
          sort_by: sortProductsBy,
        }),
        ...(productTypeId && {
          product_type: productTypeId,
        }),
        with_ads: 1,
      };
      let queryStr = Object.entries(queryObj)
        .map((item, i) => (i == 0 ? item.join("=") : `&${item.join("=")}`))
        .join("");

      this.makeRequest({
        ...(isUserLogged && { owner: MAINROLES.user }),
        url: `${APIREQUESTS.user.getProducts.url}?${queryStr}`,
        method: APIREQUESTS.user.getProducts.method,
        abortControllerSignal: this.abortController.signal,
        includeUID: true,
      })
        .then((res) => {
          console.log("✅ API response received:", {
            loadMore,
            requestPage,
            productsCount: res?.data?.length || 0,
            currentProductsPage: this.currentProductsPage,
          });

          if (Array.isArray(res?.data)) {
            const newProducts = res.data || [];
            let newOriginalDataObject = {};
            const hasReachedFullProductPages = checkPaginationEnd(res);

            if (loadMore && this.productsList?.length > 0) {
              // Update currentProductsPage to the page we just loaded
              this.currentProductsPage = requestPage;
              console.log(
                "📄 Load more: currentProductsPage updated to",
                this.currentProductsPage
              );

              newOriginalDataObject = {
                ...this.originalData,
                sortProductsBy,
                hasReachedFullProductPages,
                products: [...this.originalData?.products, ...newProducts],
              };
            } else {
              // Reset to page 1 for new search/filter
              this.currentProductsPage = 1;
              console.log("📄 New search: currentProductsPage reset to 1");

              newOriginalDataObject = {
                ...this.originalData,
                sortProductsBy,
                hasReachedFullProductPages,
                products: newProducts,
              };
            }
            
            this.originalData = newOriginalDataObject;
            
            if (loadMore && loadTrigger === "auto") {
              this.incrementAutoLoadCount();
            }

            // After pagination completes, check if we need to continue loading or restore position
            if (this._pendingProductRestore) {
              const { productId, page } = this._pendingProductRestore;
              
              this.$nextTick(() => {
                setTimeout(() => {
                  // Check if we've reached the target page
                  if (this.currentProductsPage >= page) {
                    console.log(`✅ Reached target page ${page}, restoring product position...`);
                    this.restoreProductPositionAfterPagination(productId, page);
                    this._pendingProductRestore = null; // Clear the flag
                  } else {
                    // Continue loading pages until we reach the target
                    console.log(`⏳ Still loading pages... current: ${this.currentProductsPage}, target: ${page}`);
                    this.loadPagesUntilTarget(page, productId);
                  }
                }, 400); // Wait for DOM to update with new products
              });
            }

            // Save to cache after successful API response
            if (safeProductListCache.isCacheAvailable()) {
              const cacheData = {
                products: newProducts,
                hasReachedFullProductPages,
                totalPages: res.pages_total || 1,
                totalItems: res.total || 0,
              };

              safeProductListCache.saveToCache(
                cacheKey,
                cacheData,
                targetPage,
                currentFilters,
                safeProductListCache.getCurrentScrollPosition()
              );

              // Set up automatic scroll position saving for this cache entry
              safeProductListCache.setupScrollSaving(cacheKey, targetPage);
            }
          } else {
            this.originalData = {
              ...this.originalData,
              hasReachedFullProductPages: true,
              products: [],
            };
          }
        })
        .catch((err) => {
          console.error("err", err);
          if (!err || err.code !== "axios_cancelled") {
            this.notify({
              type: "error",
              msg: err?.data?.message || "Failed to get the products data.",
            });
          }
        })
        .finally(() => {
          this.isUpdatingProducts = false;
          this.isInitiallyLoadingProducts = false;
          this.isLoadingMore = false;
          console.log("🏁 Request completed. Final state:", {
            currentProductsPage: this.currentProductsPage,
            productsCount: this.productsList?.length || 0,
            isLoadingMore: this.isLoadingMore,
            isUpdatingProducts: this.isUpdatingProducts,
          });
        });
    },
    getCategories() {
      return new Promise((resolve, reject) => {
        this.isLoadingCategories = true;
        this.makeRequest({
          url: APIREQUESTS.user.getCategories.url,
          method: APIREQUESTS.user.getCategories.method,
          abortControllerSignal: this.abortController.signal,
        })
          .then((res) => {
            this.isLoadingCategories = false;
            if (res?.status) {
              resolve();
              let categories = [];
              if (Array.isArray(res?.data)) {
                categories = res.data;
              } else {
                categories = [];
              }
              this.originalData.categories = categories;
              this.useLocalStorage({
                type: "set",
                key: "categories",
                value: categories,
              });
            } else {
              this.notify({
                type: "error",
                msg: res?.message || "Failed to get the Products data.",
              });
              reject("Failure: The promise has failed!", error);
            }
          })
          .catch((err) => {
            reject("Failure: The promise has failed!", err);
            if (!err || err.code !== "axios_cancelled") {
              this.notify({
                type: "error",
                msg: err?.data?.message || "Failed to get the Products data.",
              });
            }
            this.isLoadingCategories = false;
          });
      });
    },
    getExclusiveOffers() {
      return new Promise((resolve, reject) => {
        this.isLoadingExclusiveOffers = true;
        this.makeRequest({
          url: APIREQUESTS.user.getExclusiveOffers.url,
          method: APIREQUESTS.user.getExclusiveOffers.method,
          abortControllerSignal: this.abortController.signal,
        })
          .then((res) => {
            this.isLoadingExclusiveOffers = false;
            if (res?.status) {
              resolve();
              let exclusiveOffers = [];
              if (Array.isArray(res?.data)) {
                exclusiveOffers = res.data;
              } else {
                exclusiveOffers = [];
              }
              this.originalData.exclusiveOffers = exclusiveOffers;
            } else {
              this.notify({
                type: "error",
                msg: res?.message || "Failed to get the offers data.",
              });
              reject("Failure: The promise has failed!", error);
            }
          })
          .catch((err) => {
            console.error(err);
            reject("Failure: The promise has failed!", err);
            if (!err || err.code !== "axios_cancelled") {
              this.notify({
                type: "error",
                msg: err?.data?.message || "Failed to get the offers data.",
              });
            }
            this.isLoadingExclusiveOffers = false;
          });
      });
    },
    getStores() {
      return new Promise((resolve, reject) => {
        this.isLoadingStores = true;
        this.makeRequest({
          url: `${APIREQUESTS.user.getStores.url}${this.filterByVerifiedStoresOnly ? `?verified=${this.filterByVerifiedStoresOnly}` : ""}`,
          method: APIREQUESTS.user.getStores.method,
          abortControllerSignal: this.abortController.signal,
        })
          .then((res) => {
            this.isLoadingStores = false;
            if (res?.status) {
              resolve();
              let stores = [];
              if (Array.isArray(res?.data)) {
                stores = res.data;
              } else {
                stores = [];
              }
              this.storesList = stores;
              this.updateHomepageData({
                receivedData: {
                  ...this.savedHomepageData?.receivedData,
                  stores: stores,
                },
              });
            } else {
              this.notify({
                type: "error",
                msg: res?.message || "Failed to get the stores data.",
              });
              reject("Failure: The promise has failed!", error);
            }
          })
          .catch((err) => {
            console.error(err);
            reject("Failure: The promise has failed!", err);
            if (!err || err.code !== "axios_cancelled") {
              this.notify({
                type: "error",
                msg: err?.data?.message || "Failed to get the stores data.",
              });
            }
            this.isLoadingStores = false;
          });
      });
    },
    changeFilterByVerifiedStoresOnlyState(newState) {
      this.filterByVerifiedStoresOnly = +newState;
      this.getStores();
    },
    changeProductByTypeValue(newValue) {
      const { value } = newValue || {};
      const oldProductType = this.filterProductsByType;
      
      // Clear product navigation context when filter changes
      this.clearStoredProductNavigationContext();
      
      this.originalData = {
        ...this.originalData,
        filterProductsByType: value !== "all" ? newValue : null,
      };
      
      // Clear product positions when product type changes
      if (oldProductType !== (value !== "all" ? newValue : null)) {
        const oldCacheKey = safeProductListCache.generateCacheKey(
          this.getCurrentFilters(),
          this.originalData?.sortProductsBy || "all",
          oldProductType
        );
        safeProductListCache.clearProductPositions(oldCacheKey);
        console.log("🗑️ Cleared product positions due to product type change");
      }
      
      this.getProducts({ updateMode: true });
    },
    getAll() {
      this.getCategories().then(() => {
        this.getExclusiveOffers();
      });
      this.getStores();
      // Force initial products load with explicit parameters
      console.log("🔄 getAll: Making initial products call");
      this.getProducts({
        loadMore: false,
        sortProductsBy: "all",
        isInitiallyLoading: true,
        updateMode: false,
        forceInitialLoad: true,
      });
      // Get favorite IDs for logged-in users
      const isUserLogged =
        this.useLocalStorage({ type: "get", key: "user" })?.token || false;
      if (isUserLogged) {
        this.getFavorites();
      }
    },
    getFavorites() {
      return new Promise((resolve, reject) => {
        this.makeRequest({
          owner: MAINROLES.user,
          url: APIREQUESTS.user.getFavoriteIDs.url,
          method: APIREQUESTS.user.getFavoriteIDs.method,
        })
          .then((res) => {
            //check response
            if (!res.status) {
              //not ok
              reject();
              this.notify({
                type: "error",
                msg: res?.message || this.$t("general.failed"),
              });
            } else {
              // save the favorites in the localStorage
              this.useLocalStorage({
                type: "set",
                key: "favoriteProducts",
                value: res.data || [],
              });
              resolve();
            }
          })
          .catch((err) => {
            reject();
            this.notify({
              type: "error",
              msg: err?.data?.message || this.$t("general.failed"),
            });
          });
      });
    },

    /**
     * Get current filter state for caching purposes
     */
    getCurrentFilters() {
      return {
        search: this.searchInpt || "",
        category:
          this.subCategory?.selected?.id ||
          this.mainCategory?.selected?.id ||
          "",
        store: this.stores?.selected?.id || "",
        governorate: this.governorates?.selected?.id || "",
        area: this.areas?.selected?.id || "",
        priceMin: this.rangeValue?.min || "",
        priceMax: this.rangeValue?.max || "",
        isNegotiable: this.isNegotiable || false,
        spaceMin: this.space?.from || "",
        spaceMax: this.space?.to || "",
        date: this.date?.selected?.type || "desc",
      };
    },

    /**
     * Clear cache for specific filters or all cache
     */
    clearProductCache(cacheKey = null) {
      if (cacheKey) {
        safeProductListCache.clearCacheByKey(cacheKey);
      } else {
        safeProductListCache.clearAllCache();
      }
    },

    /**
     * Get cache statistics for debugging
     */
    getCacheStats() {
      return safeProductListCache.getCacheStats();
    },

    /**
     * Clear product cache specifically for homepage navigation
     * Can be called when user clicks on categories
     */
    clearCacheForHomepageNavigation() {
      console.log("🚫 Clearing product cache for homepage navigation");
      safeProductListCache.clearAllCache();
      console.log("✅ Cache cleared before homepage category navigation");
    },

    markAsNavigation() {
      this.useLocalStorage({ type: "set", key: "isNavigation", value: true });
      // Also mark in cache system
      if (this.safeProductListCache) {
        this.safeProductListCache.markAsNavigation();
      }
    },
    initializePage() {
      if (this.useLocalStorage({ type: "get", key: "isNavigation" }) === true) {
        this.useLocalStorage({
          type: "set",
          key: "isNavigation",
          value: false,
        });
        this.markAsNavigation();
      }
    },
    setupNavigationDetection() {
      // Use the cache system's navigation detection
      if (this.safeProductListCache) {
        this.safeProductListCache.setupNavigationDetection();
      }
    },
    removeNavigationDetection() {
      // Use the cache system's navigation detection
      if (this.safeProductListCache) {
        this.safeProductListCache.removeNavigationDetection();
      }
    },
    // Check if we're coming from navigation (back/forward) vs. filter update
    isNavigationFromBrowser() {
      // Check if we have navigation state in sessionStorage
      const navigationState = sessionStorage.getItem("navigationType");
      return navigationState === "navigation";
    },

    // Check if we should use cache for current request
    shouldUseCacheForRequest(updateMode, loadMore) {
      // Always try cache if not in update mode
      if (!updateMode) {
        return true;
      }

      // In update mode, only try cache if:
      // 1. We're coming from browser navigation
      // 2. We don't have products currently displayed
      // 3. This is not a load more request
      return (
        this.isNavigationFromBrowser() &&
        this.productsList?.length === 0 &&
        !loadMore
      );
    },
    // Save product card position when clicked for precise restoration
    saveProductCardPosition(productId) {
      if (!this.safeProductListCache) return;

      try {
        // Clear any existing context before saving new one
        this.clearStoredProductNavigationContext();
        
        // CRITICAL: Capture scroll position IMMEDIATELY before any navigation or DOM changes
        const currentScrollX = window.scrollX || window.pageXOffset || 0;
        const currentScrollY = window.scrollY || window.pageYOffset || 0;

        // Get current cache key and page
        const currentFilters = this.getCurrentFilters();
        const cacheKey = this.safeProductListCache.generateCacheKey(
          currentFilters,
          this.originalData?.sortProductsBy || "all",
          this.productType
        );
        const currentPage = this.currentProductsPage || 1;

        // Get the product card element
        const productCard = document.querySelector(
          `[data-product-id="${productId}"]`
        );
        if (!productCard) {
          console.log(`❌ Product card element not found for ID: ${productId}`);
          return;
        }

        // Get the product card position relative to the viewport
        // Use the captured scroll position, not window.scrollX/Y which might have changed
        const cardRect = productCard.getBoundingClientRect();
        const absoluteY = cardRect.top + currentScrollY;
        const absoluteX = cardRect.left + currentScrollX;
        
        // Validate that we have a valid position (not 0,0 unless product is actually at top)
        // If scroll is 0 and card top is negative or very small, it might be invalid
        if (currentScrollY === 0 && cardRect.top < -100) {
          console.warn(`⚠️ Suspicious position detected for product ${productId}: scrollY=0 but card top=${cardRect.top}. Skipping save.`);
          return;
        }
        
        const cardPosition = {
          x: absoluteX,
          y: absoluteY,
          pageScrollX: currentScrollX,
          pageScrollY: currentScrollY,
          cardHeight: cardRect.height,
          cardWidth: cardRect.width,
        };

        // Save the product card position
        const saved = this.safeProductListCache.saveProductCardPosition(
          cacheKey,
          currentPage,
          productId,
          cardPosition
        );

        if (saved) {
          console.log(
            `📍 Product card position saved for ${productId}:`,
            cardPosition
          );
          
          // Store product ID and context for restoration when returning from product page
          this.storeProductNavigationContext(productId, cacheKey, currentPage);
        }
      } catch (error) {
        console.error("Failed to save product card position:", error);
      }
    },

    // Store product navigation context for restoration
    storeProductNavigationContext(productId, cacheKey, page) {
      if (typeof window === 'undefined') return;
      
      try {
        const context = {
          productId,
          cacheKey,
          page,
          timestamp: Date.now(),
          portalPage: 'homepage'
        };
        sessionStorage.setItem('lastClickedProduct', JSON.stringify(context));
        console.log(`💾 Stored product navigation context: ${productId}`);
      } catch (error) {
        console.error('Failed to store product navigation context:', error);
      }
    },

    // Get stored product navigation context
    getStoredProductNavigationContext() {
      if (typeof window === 'undefined') return null;
      
      try {
        const stored = sessionStorage.getItem('lastClickedProduct');
        if (!stored) return null;
        
        const context = JSON.parse(stored);
        // Check if context is still valid (not too old - 1 hour)
        const age = Date.now() - (context.timestamp || 0);
        const maxAge = 60 * 60 * 1000; // 1 hour
        if (age > maxAge) {
          sessionStorage.removeItem('lastClickedProduct');
          return null;
        }
        return context;
      } catch (error) {
        console.error('Failed to get stored product navigation context:', error);
        return null;
      }
    },

    // Clear stored product navigation context
    clearStoredProductNavigationContext() {
      if (typeof window === 'undefined') return;
      sessionStorage.removeItem('lastClickedProduct');
    },

    // Try to restore product position after page loads
    // Strategy: Load the correct page first, then restore position after pagination completes
    tryRestoreProductPosition() {
      if (typeof window === 'undefined' || !this.safeProductListCache) return;
      
      // Prevent multiple restoration attempts
      if (this._isRestoringPosition) {
        console.log('⏸️ Restoration already in progress, skipping duplicate attempt');
        return;
      }
      
      const context = this.getStoredProductNavigationContext();
      if (!context) {
        console.log('🔍 No product navigation context found');
        return;
      }
      
      console.log('🔍 Found product navigation context:', context);
      
      // Check if we're on the correct portal page
      if (context.portalPage !== 'homepage') {
        console.log(`❌ Wrong portal page: ${context.portalPage}, expected homepage`);
        return;
      }
      
      // Check if products are loaded
      if (!this.productsList || this.productsList.length === 0) {
        console.log('⏳ Products not loaded yet, will retry restoration');
        setTimeout(() => {
          this.tryRestoreProductPosition();
        }, 500);
        return;
      }
      
      console.log(`✅ Products loaded: ${this.productsList.length}`);
      
      // Verify cache key still matches (filters/sorting haven't changed)
      const currentFilters = this.getCurrentFilters();
      const currentCacheKey = this.safeProductListCache.generateCacheKey(
        currentFilters,
        this.originalData?.sortProductsBy || "all",
        this.productType
      );
      
      if (context.cacheKey !== currentCacheKey) {
        console.log('🗑️ Cache key mismatch, clearing stored product context');
        this.clearStoredProductNavigationContext();
        return;
      }
      
      // Set restoration flag
      this._isRestoringPosition = true;
      
      const savedPage = context.page || 1;
      const currentPage = this.currentProductsPage || 1;
      
      // Strategy: Load the correct page first if needed, then restore position after pagination
      if (savedPage !== currentPage && savedPage > 1) {
        console.log(`📄 Product was on page ${savedPage}, current page is ${currentPage}. Loading page ${savedPage} first...`);
        
        // Set flag to restore position after pagination completes
        this._pendingProductRestore = {
          productId: context.productId,
          page: savedPage
        };
        
        // Load pages sequentially until we reach the target page
        // Start from current page and load more until we reach target
        this.loadPagesUntilTarget(savedPage, context.productId);
      } else {
        // Already on correct page, just restore position after products are loaded
        this.$nextTick(() => {
          setTimeout(() => {
            this.restoreProductPositionAfterPagination(context.productId, savedPage);
          }, 500);
        });
      }
    },

    // Load pages sequentially until we reach the target page
    loadPagesUntilTarget(targetPage, productId) {
      const currentPage = this.currentProductsPage || 1;
      
      if (currentPage >= targetPage) {
        // We've reached the target page, restoration will happen in API response handler
        console.log(`✅ Reached target page ${targetPage}`);
        return;
      }
      
      // Load next page
      console.log(`📄 Loading page ${currentPage + 1} to reach target page ${targetPage}...`);
      
      this.getProducts({
        loadMore: true,
        sortProductsBy: this.originalData?.sortProductsBy || "all",
        isInitiallyLoading: false,
        productType: this.productType,
        updateMode: false,
        forceInitialLoad: false,
        loadTrigger: "manual"
      });
      
      // Continue loading if needed (will be handled in API response)
    },

    // Restore product position after pagination has completed
    restoreProductPositionAfterPagination(productId, page) {
      if (!this.safeProductListCache) {
        this._isRestoringPosition = false;
        return;
      }
      
      try {
        console.log(`🎯 Restoring product position for ${productId} on page ${page} after pagination`);
        
        // Verify we're on the correct page
        if (this.currentProductsPage !== page) {
          console.log(`⚠️ Page mismatch: expected ${page}, got ${this.currentProductsPage}. Retrying...`);
          // Retry after a bit more delay
          setTimeout(() => {
            this.restoreProductPositionAfterPagination(productId, page);
          }, 500);
          return;
        }
        
        // Check if products list is empty
        if (!this.productsList || this.productsList.length === 0) {
          console.log(`⏳ Products list is empty, retrying...`);
          setTimeout(() => {
            this.restoreProductPositionAfterPagination(productId, page);
          }, 500);
          return;
        }
        
        // Verify product still exists in current filtered list
        const productExists = this.productsList.some(p => p.id === productId);
        if (!productExists) {
          console.log(`❌ Product ${productId} not found in current products list. Clearing context.`);
          this.clearStoredProductNavigationContext();
          this._isRestoringPosition = false;
          return;
        }
        
        // Check if product exists in DOM
        const productElement = document.querySelector(`[data-product-id="${productId}"]`);
        if (!productElement) {
          console.log(`❌ Product ${productId} not found in DOM. Products may still be loading.`);
          // Retry after delay (max 3 attempts)
          const retryCount = this._restoreRetryCount || 0;
          if (retryCount < 3) {
            this._restoreRetryCount = retryCount + 1;
            setTimeout(() => {
              this.restoreProductPositionAfterPagination(productId, page);
            }, 500);
          } else {
            console.log(`❌ Max retries reached. Clearing context.`);
            this.clearStoredProductNavigationContext();
            this._isRestoringPosition = false;
            this._restoreRetryCount = 0;
          }
          return;
        }
        
        // Reset retry count
        this._restoreRetryCount = 0;
        
        // Now restore the position
        const restored = this.restoreToProductCard(productId);
        
        if (restored) {
          console.log(`✅ Successfully restored to product ${productId}`);
          this.clearStoredProductNavigationContext();
          // Re-enable package restoration after product restoration completes
          if (this.safeProductListCache) {
            setTimeout(() => {
              // Allow package to handle future navigation
              console.log('📦 Product restoration complete, re-enabling package restoration');
            }, 1000);
          }
        } else {
          console.log(`❌ Failed to restore product position. Clearing context.`);
          this.clearStoredProductNavigationContext();
        }
        
        this._isRestoringPosition = false;
      } catch (error) {
        console.error('Error in restoreProductPositionAfterPagination:', error);
        this.clearStoredProductNavigationContext();
        this._isRestoringPosition = false;
        this._restoreRetryCount = 0;
      }
    },

    // Restore scroll to a specific product card
    restoreToProductCard(productId) {
      if (!this.safeProductListCache) return false;

      try {
        // Get current cache key and page
        const currentFilters = this.getCurrentFilters();
        const cacheKey = this.safeProductListCache.generateCacheKey(
          currentFilters,
          this.originalData?.sortProductsBy || "all",
          this.productType
        );
        const currentPage = this.currentProductsPage || 1;

        // Restore to the specific product card
        const restored = this.safeProductListCache.restoreToProductCard(
          cacheKey,
          currentPage,
          productId
        );

        if (restored) {
          console.log(`🎯 Scroll restored to product card: ${productId}`);
        } else {
          console.log(
            `❌ Failed to restore scroll to product card: ${productId}`
          );
        }

        return restored;
      } catch (error) {
        console.error("Failed to restore to product card:", error);
        return false;
      }
    },
    testHomepageCache() {
      console.log("Testing Homepage Cache...");
      const currentFilters = this.getCurrentFilters();
      const cacheKey = safeProductListCache.generateCacheKey(
        currentFilters,
        this.sortProductsBy || "all",
        this.filterProductsByType
      );
      const targetPage = 1;

      const hasCache = safeProductListCache.hasValidCache(
        cacheKey,
        targetPage,
        currentFilters
      );
      console.log("Has cache?", hasCache);

      if (hasCache) {
        console.log("✅ Cache found, attempting smart retrieval...");
        const result = safeProductListCache.smartCacheRetrieval(
          cacheKey,
          targetPage,
          currentFilters,
          {
            autoRestoreScroll: true, // Keep true for test methods (not user-initiated sorting)
            autoDisplayProducts: true,
            onDataRetrieved: (cachedData) => {
              console.log(
                "🎯 CALLBACK EXECUTED: Displaying cached products:",
                cachedData.products.length
              );
              this.originalData = {
                ...this.originalData,
                sortProductsBy: this.sortProductsBy || "all",
                hasReachedFullProductPages:
                  cachedData.hasReachedFullProductPages || false,
                products: cachedData.products || [],
              };
              this.currentProductsPage = 1;
              this.isInitiallyLoadingProducts = false;
              this.isUpdatingProducts = false;
              this.cachedDataDisplayed = true;
              console.log(
                "✅ Cached data displayed successfully in testHomepageCache"
              );
            },
            onScrollRestored: (scrollPosition) => {
              console.log(
                "📜 Scroll position restored in testHomepageCache:",
                scrollPosition
              );
            },
            onComplete: (cachedData) => {
              console.log("🎉 Cache retrieval completed in testHomepageCache");
            },
          }
        );
        if (result.success) {
          console.log("🎉 Cache displayed successfully in testHomepageCache");
        } else {
          console.log("❌ Cache display failed in testHomepageCache");
        }
      } else {
        console.log("❌ No cache found in testHomepageCache");
      }
    },
    clearHomepageCache() {
      console.log("Clearing Homepage Cache...");
      safeProductListCache.clearAllCache();
      console.log("Homepage Cache cleared.");
    },
    testCachedDataDisplay() {
      console.log("Testing Cached Data Display...");
      const currentFilters = this.getCurrentFilters();
      const cacheKey = safeProductListCache.generateCacheKey(
        currentFilters,
        this.sortProductsBy || "all",
        this.filterProductsByType
      );
      const targetPage = 1;

      const hasCache = safeProductListCache.hasValidCache(
        cacheKey,
        targetPage,
        currentFilters
      );
      console.log("Has cache?", hasCache);

      if (hasCache) {
        console.log("✅ Cache found, attempting smart retrieval...");
        const result = safeProductListCache.smartCacheRetrieval(
          cacheKey,
          targetPage,
          currentFilters,
          {
            autoRestoreScroll: true, // Keep true for test methods (not user-initiated sorting)
            autoDisplayProducts: true,
            onDataRetrieved: (cachedData) => {
              console.log(
                "🎯 CALLBACK EXECUTED: Displaying cached products..."
              );
              this.originalData = {
                ...this.originalData,
                sortProductsBy: this.sortProductsBy || "all",
                hasReachedFullProductPages:
                  cachedData.hasReachedFullProductPages || false,
                products: cachedData.products || [],
              };
              this.currentProductsPage = 1;
              this.isInitiallyLoadingProducts = false;
              this.isUpdatingProducts = false;
              this.cachedDataDisplayed = true;
              console.log(
                "✅ Cached data displayed successfully in testCachedDataDisplay"
              );
            },
            onScrollRestored: (scrollPosition) => {
              console.log(
                "📜 Scroll position restored in testCachedDataDisplay:",
                scrollPosition
              );
            },
            onComplete: (cachedData) => {
              console.log(
                "🎉 Cache retrieval completed in testCachedDataDisplay"
              );
            },
          }
        );
        if (result.success) {
          console.log(
            "🎉 Cache displayed successfully in testCachedDataDisplay"
          );
        } else {
          console.log("❌ Cache display failed in testCachedDataDisplay");
        }
      } else {
        console.log("❌ No cache found in testCachedDataDisplay");
      }
    },
    checkSavedHomepageData() {
      console.log("Checking Saved Homepage Data...");
      console.log("Current savedHomepageData:", this.savedHomepageData);
      console.log("Current localStorageCopy:", this.localStorageCopy);
      console.log("Current originalData:", this.originalData);
      console.log("Current productsList:", this.productsList);
      console.log("Current currentPage:", this.currentPage);
      console.log("Current maxPages:", this.maxPages);
      console.log("Current currentProductsPage:", this.currentProductsPage);
      console.log("Current isLoading:", this.isLoading);
      console.log("Current isLoadingMore:", this.isLoadingMore);
      console.log("Current isLoadingCategories:", this.isLoadingCategories);
      console.log("Current isLoadingStores:", this.isLoadingStores);
      console.log("Current isLoadingProducts:", this.isLoadingProducts);
      console.log("Current isUpdatingProducts:", this.isUpdatingProducts);
      console.log(
        "Current isLoadingExclusiveOffers:",
        this.isLoadingExclusiveOffers
      );
      console.log(
        "Current isInitiallyLoadingProducts:",
        this.isInitiallyLoadingProducts
      );
      console.log("Current currentLocation:", this.currentLocation);
      console.log("Current isHomePageMounted:", this.isHomePageMounted);
      console.log("Current cachedDataDisplayed:", this.cachedDataDisplayed);
    },

    /**
     * Detect if this is a browser refresh (F5, Ctrl+R, etc.)
     */
    detectBrowserRefresh() {
      if (!process.client) return false;

      try {
        // Method 1: Modern Navigation API
        const navigationEntries = performance.getEntriesByType("navigation");
        if (navigationEntries.length > 0) {
          const navigation = navigationEntries[0];
          if (navigation.type === "reload") {
            console.log("🔄 Browser refresh detected via Navigation API");
            return true;
          }
        }

        // Method 2: Legacy performance.navigation
        if (performance.navigation && performance.navigation.type === 1) {
          console.log("🔄 Browser refresh detected via legacy API");
          return true;
        }

        // Method 3: Check document.wasDiscarded
        if (document.wasDiscarded) {
          console.log("🔄 Browser refresh detected via document.wasDiscarded");
          return true;
        }

        // Method 4: Check session storage markers
        const cacheCleared = sessionStorage.getItem("cacheClearedOnRefresh");
        const refreshDetected = sessionStorage.getItem("detectedReload");

        if (cacheCleared === "true" || refreshDetected === "true") {
          console.log("🔄 Browser refresh detected via session markers");
          return true;
        }

        // Method 5: Check timing patterns
        const lastPageUnload = sessionStorage.getItem("lastPageUnload");
        const currentTime = Date.now();
        if (lastPageUnload && currentTime - parseInt(lastPageUnload) < 1000) {
          console.log("🔄 Browser refresh detected via timing analysis");
          return true;
        }

        return false;
      } catch (error) {
        console.warn("Error detecting browser refresh:", error);
        return false;
      }
    },
  },
  async created() {
    if (!process.client) {
      return;
    }
    // Enhanced browser refresh detection and cache clearing
    console.log("🏠 Homepage opened - checking for browser refresh");

    // Check if we have a valid product navigation context BEFORE clearing cache
    const productContext = this.getStoredProductNavigationContext();
    const hasValidProductContext = productContext && 
                                   productContext.portalPage === 'homepage' &&
                                   (Date.now() - (productContext.timestamp || 0)) < (60 * 60 * 1000); // Less than 1 hour old

    // Method 1: Check if this might be a browser refresh using multiple indicators
    const isBrowserRefresh = this.detectBrowserRefresh();

    if (isBrowserRefresh) {
      console.log("🔄 Browser refresh detected - clearing all cache");
      safeProductListCache.clearAllCache();
    } else {
      console.log("🔄 Navigation detected - clearing product cache");
      
      // If we have a valid product context, preserve product positions for restoration
      if (hasValidProductContext) {
        console.log("💾 Preserving product positions for restoration");
        // Clear cache but preserve product positions for the specific context
        safeProductListCache.clearCachePreserveProductPositions(
          productContext.cacheKey,
          productContext.page
        );
      } else {
        // No valid context, clear everything
        safeProductListCache.clearAllCache();
      }
    }

    console.log("✅ Cache cleared");

    // FIXED: Always reset pagination to ensure we start from page 1 on homepage load
    this.currentProductsPage = 1;
    this.currentPage = 1;
    console.log("✅ Pagination reset to page 1");

    // this.refreshFirebaseToken();
    const isUserLogged =
      this.useLocalStorage({ type: "get", key: "user" })?.token || false;
    if (isUserLogged) {
      this.getProfile();
    }

    // Check for saved homepage data first before any API calls
    console.log("🔍 Checking for saved homepage data in created()...");

    // Check if we have saved homepage data
    const { location = { lat: "", lng: "" }, receivedData = {} } =
      this.savedHomepageData || {};

    if (receivedData && Object.keys(receivedData?.data || {})?.length > 0) {
      console.log("✅ Found saved homepage data, displaying immediately...");

      // Set location if available
      if (location?.lat && location?.lng) {
        const { lat, lng } = location;
        this.currentLocation = `${lat},${lng}`;
      }

      // Display saved data immediately
      this.isLoading = false;
      this.originalData = receivedData.data;
      this.storesList = receivedData.stores || [];
      this.currentPage = parseInt(receivedData.current_page) || 1;
      this.maxPages = parseInt(receivedData.pages_total) || 10;
      // FIXED: Reset products pagination to page 1 to prevent starting from wrong page
      this.currentProductsPage = 1;

      // Ensure products is always an array
      if (
        this.originalData?.products &&
        !Array.isArray(this.originalData.products)
      ) {
        console.warn("⚠️ Products is not an array, converting...");
        this.originalData.products = [];
      }

      // Mark that we've displayed saved data to prevent override
      this.cachedDataDisplayed = true;

      // Ensure the data is properly saved to global store
      this.updateHomepageData({
        receivedData: {
          ...this.savedHomepageData?.receivedData,
          data: this.originalData,
          stores: this.storesList,
          current_page: this.currentPage,
          pages_total: this.maxPages,
        },
      });

      console.log("✅ Saved homepage data displayed successfully in created()");
      console.log(
        "- Products count:",
        this.originalData?.products?.length || 0
      );
      console.log(
        "- Products is array?",
        Array.isArray(this.originalData?.products)
      );
      console.log("- Stores count:", this.storesList?.length || 0);
      console.log(
        "- Categories count:",
        this.originalData?.categories?.length || 0
      );
      console.log(
        "- Exclusive offers count:",
        this.originalData?.exclusiveOffers?.length || 0
      );
      console.log("- Original data structure:", this.originalData);

      // Center active group button if needed
      const sortProductsBy = receivedData?.data?.sortProductsBy;
      if (sortProductsBy) {
        this.$nextTick(() => {
          const groupItem = document.getElementById(
            `group-${sortProductsBy?.id}`
          );
          this.centerActiveGroupButton(groupItem);
        });
      }

      return; // Exit early, no need for API calls
    } else {
      console.log(
        "❌ No saved homepage data found in created(), proceeding with normal flow"
      );
    }

    // Original logic for location and data handling (only if no saved data was displayed)
    if (!this.cachedDataDisplayed) {
      // prevents reloading the pre-loaded data in the same session
      if (location?.lat && location?.lng) {
        const { lat, lng } = location;
        this.currentLocation = `${lat},${lng}`;
      } else {
        try {
          //check geolocation permissions
          let permissionStatus = await navigator.permissions.query({
            name: "geolocation",
          });
          permissionStatus.state == "granted" &&
            (await this.sendLocationWithGetHomeApi());
        } catch {}
      }
      //handling cacehing
      if (receivedData && Object.keys(receivedData?.data || {})?.length > 0) {
        this.isLoading = false;
        this.originalData = receivedData.data;
        this.storesList = receivedData.stores || [];
        this.currentPage = parseInt(receivedData.current_page);
        this.maxPages = parseInt(receivedData.pages_total);
        // FIXED: Reset products pagination to page 1 to prevent starting from wrong page
        this.currentProductsPage = 1;
        const sortProductsBy = receivedData?.data?.sortProductsBy;
        this.$nextTick(() => {
          const groupItem = document.getElementById(
            `group-${sortProductsBy?.id}`
          );
          this.centerActiveGroupButton(groupItem);
        });
      } else {
        // if data is not saved, fetch it
        this.fetchNewHome({}).then(() => {
          this.getAll();
        });
      }
    } else {
      console.log(
        "✅ Skipping original data handling since saved data was already displayed"
      );
    }
  },
  watch: {
    originalData: {
      handler(val) {
        this.updateHomepageData({
          receivedData: {
            ...this.savedHomepageData?.receivedData,
            data: val,
          },
        });
      },
    },
    storesList: {
      handler(val) {
        this.updateHomepageData({
          receivedData: {
            ...this.savedHomepageData?.receivedData,
            stores: val,
          },
        });
      },
    },
    currentlySelectedLanguage() {
      this.$nextTick(() => {
        setTimeout(() => {
          const activeButtonEl = document.querySelector(`#groupsList .active`);
          this.centerActiveGroupButton(activeButtonEl);
        }, 10);
      });
    },
  },
  mounted() {
    // Mark this as navigation to preserve cache
    this.markAsNavigation();

    // Set up navigation detection
    this.setupNavigationDetection();

    // Check if we're coming from browser navigation
    if (this.isNavigationFromBrowser()) {
      console.log(
        "🔄 Detected browser navigation to homepage - will try cache first"
      );
    }

    // Initialize the page
    this.initializePage();
    
    // Priority system: Check for product card restoration first
    // If product context exists, it takes priority over nuxt-scroll-restoration package
    const productContext = this.getStoredProductNavigationContext();
    if (productContext && productContext.portalPage === 'homepage') {
      // Product context exists - prevent package restoration and use custom restoration
      if (this.safeProductListCache) {
        // Prevent package from interfering during product restoration
        this.safeProductListCache.preventScrollInterference(5000); // 5 seconds
        this.safeProductListCache.disableAutoScrolling(5000);
      }
      
      // Try to restore product position after page loads
      if (this.productsList && this.productsList.length > 0) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.tryRestoreProductPosition();
          }, 100); // Shorter delay since we're preventing interference
        });
      } else {
        // Wait for products to load
        this.$nextTick(() => {
          setTimeout(() => {
            this.tryRestoreProductPosition();
          }, 1000);
        });
      }
    } else {
      // No product context - let nuxt-scroll-restoration package handle it
      // Package will automatically restore scroll position
      console.log('📦 No product context, using nuxt-scroll-restoration package');
    }
  },
  beforeUnmount() {
    // Remove navigation detection
    this.removeNavigationDetection();

    // Remove scroll saving
    safeProductListCache.removeScrollSaving();
  },
};
</script>

<style lang="scss">
/* Ad section containment - prevents AdSense iframe overlay from blocking product clicks */
.ad-section-container {
  position: relative;
  z-index: 0;
  overflow: hidden;
  isolation: isolate;
  contain: paint;
}

#userHomepage {
  .slider-content {
    @media screen and (max-width: 760px) {
      padding: 0 17px;
    }
  }
}
</style>
